import apiClient from '../lib/apiClient';

// Get attendance records
const getAttendanceRecords = async (filters = {}) => {
  try {
    const result = await apiClient.get('/attendance', filters);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch attendance records' };
  }
};

// Clock in
const clockIn = async (data) => {
  try {
    const result = await apiClient.post('/attendance/clockIn', data);
    if (!result.success) {
      const err = String(result.error || '');
      if (err.includes('notifications') && err.includes("doesn't exist")) {
        return {
          success: false,
          error:
            'Server missing notifications table. Admin: upload api/fix_notifications.php then open https://www.inchihrms.com/api/fix_notifications.php — or run CREATE TABLE in phpMyAdmin (lvsuohen_erp).',
        };
      }
    }
    return result;
  } catch (error) {
    const err = String(error.message || '');
    if (err.includes('notifications')) {
      return {
        success: false,
        error:
          'Clock-in blocked: notifications table missing. Admin: upload api/fix_notifications.php and visit https://www.inchihrms.com/api/fix_notifications.php',
      };
    }
    return { success: false, error: error.message || 'Failed to clock in' };
  }
};

// Clock out
const clockOut = async (data) => {
  try {
    const result = await apiClient.post('/attendance/clockOut', data);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to clock out' };
  }
};

// Get today's attendance status
const getTodayStatus = async (employeeId) => {
  try {
    const result = await apiClient.get('/attendance/todayStatus', { employee_id: employeeId });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch today status' };
  }
};

// Start break
const startBreak = async (data) => {
  try {
    const result = await apiClient.post('/attendance/startBreak', data);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to start break' };
  }
};

// End break
const endBreak = async (data) => {
  try {
    const result = await apiClient.post('/attendance/endBreak', data);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to end break' };
  }
};

// Export attendance to CSV or Excel
const exportAttendance = async (filters = {}, format = 'csv') => {
  try {
    const exportFilters = { ...filters, format };
    const queryParams = new URLSearchParams(exportFilters).toString();
    const baseURL = import.meta.env.VITE_API_BASE_URL ||
      import.meta.env.VITE_ERP_API_URL ||
      (window.location.hostname === 'localhost' ? `http://${window.location.hostname}:8000/api` : window.location.origin + '/api');
    const url = `${baseURL}/attendance/export${queryParams ? '?' + queryParams : ''}`;

    const token = localStorage.getItem('erp_auth_token');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    const extension = format === 'excel' ? 'xls' : 'csv';
    link.download = `attendance_report_${new Date().toISOString().split('T')[0]}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || 'Failed to export attendance' };
  }
};

// Get attendance summary for a period
const getAttendanceSummary = async (employeeId, startDate, endDate) => {
  try {
    const params = typeof employeeId === 'object' ? employeeId : {
      employee_id: employeeId,
      start_date: startDate,
      end_date: endDate
    };
    const result = await apiClient.get('/attendance/summary', params);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch attendance summary' };
  }
};

// Get monthly attendance summary across employees
const getMonthlyAttendanceSummary = async (params = {}) => {
  try {
    const result = await apiClient.get('/attendance/summary', params);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch monthly attendance summary' };
  }
};

// Get monthly attendance matrix for AttendanceTracker
const getAttendanceMatrix = async (params = {}) => {
  try {
    const result = await apiClient.get('/attendance', params);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch attendance matrix' };
  }
};

// Save / update a single day attendance record
const saveAttendanceRecord = async (data) => {
  try {
    const result = await apiClient.post('/attendance/save', data);
    if (!result.success && result.error && result.error.includes('Action not found')) {
      return await apiClient.post('/attendance', data);
    }
    return result;
  } catch (error) {
    try {
      return await apiClient.post('/attendance', data);
    } catch (err2) {
      return { success: false, error: error.message || 'Failed to save attendance record' };
    }
  }
};

// Bulk import attendance records
const bulkImportAttendance = async (records) => {
  try {
    const result = await apiClient.post('/attendance/bulk', { records });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to bulk import attendance' };
  }
};

// Get Live Attendance data
const getLiveAttendance = async (params = {}) => {
  try {
    const result = await apiClient.get('/attendance/live', params);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch live attendance data' };
  }
};

// Get Live Attendance counts
const getLiveAttendanceCounts = async (params = {}) => {
  try {
    const result = await apiClient.get('/attendance/live/counts', params);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to fetch live attendance counts' };
  }
};

const requestRegularization = async (data) => {
  try {
    return await apiClient.post('/attendance-regularization', data);
  } catch (error) {
    return { success: false, error: error.message || 'Failed to submit regularization request' };
  }
};

export default {
  getAttendanceRecords,
  clockIn,
  clockOut,
  startBreak,
  endBreak,
  getTodayStatus,
  exportAttendance,
  getAttendanceSummary,
  getMonthlyAttendanceSummary,
  getAttendanceMatrix,
  saveAttendanceRecord,
  bulkImportAttendance,
  getLiveAttendance,
  getLiveAttendanceCounts,
  requestRegularization
};



